/**
 * Super Admin impersonation — admin.gosnel.com opens local.gosnel.com with a vendor token.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { VENDOR_KEY } from '../constants';
import { useAuth } from '../auth';
import { formatApiError } from '../api';

const IMPERSONATION_KEY = 'lp_admin_impersonation';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = searchParams.get('token');
      const vendorId = searchParams.get('vendor_id');
      if (!token) {
        window.location.assign('https://auth.gosnel.com');
        return;
      }
      try {
        if (vendorId) localStorage.setItem('lp_vendor_id', vendorId);
        localStorage.setItem(IMPERSONATION_KEY, 'true');
        await loginWithToken(token);
        if (!cancelled) navigate('/orders', { replace: true });
      } catch (err) {
        localStorage.removeItem(VENDOR_KEY);
        localStorage.removeItem(IMPERSONATION_KEY);
        if (err?.code === 'NOT_LOCAL_PARTNER') {
          if (!cancelled) {
            setError(
              'This account is not a local partner shop. Use partner.gosnel.com for restaurants.'
            );
          }
          return;
        }
        if (!cancelled) {
          setError(formatApiError(err, 'Could not open shop account'));
          setTimeout(() => {
            window.location.assign('https://auth.gosnel.com');
          }, 2500);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, loginWithToken]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        px: 2,
        bgcolor: '#f8fafc',
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ maxWidth: 420 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={48} sx={{ mb: 2, color: '#0f766e' }} />
          <Typography variant="h6" color="text.secondary">
            Opening shop account…
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.75 }}>
            Admin access mode
          </Typography>
        </>
      )}
    </Box>
  );
}
